<script setup>
import { useFlash } from '@/Hooks/Shared/useFlash.js'
import { watch, onMounted } from 'vue'
import { Icon } from '@aryeo/runway'
import { usePage } from '@inertiajs/vue3'

const page = usePage()

const { addFlashFromBackend, flashes, flashSuccess, flashError, removeFlash } = useFlash()

const laravelErrorBagsToIgnore = ['strong_customer_authentication']

function addFlashMessages(pageProps) {
  const flash = pageProps.flash

  if (!flash) {
    return
  }

  if (flash.success) {
    if (typeof flash.success === 'string') {
      flashSuccess(flash.success)
    } else if (flash.success.message) {
      addFlashFromBackend({
        id: flash.success.id,
        message: flash.success.message,
        type: 'success',
      })
    }
  }
  if (flash.error) {
    if (typeof flash.error === 'string') {
      flashError(flash.error)
    } else if (flash.error.message) {
      addFlashFromBackend({
        id: flash.error.id,
        message: flash.error.message,
        type: 'error',
      })
    }
  }
}

onMounted(() => {
  addFlashMessages(page)
})

watch(page, (page) => {
  if (page.props.errors) {
    if (shouldBypassErrors(page.props.errors)) {
      return
    }

    const errorCount = Object.keys(page.props.errors).length

    if (errorCount > 0) {
      let msg = 'There is one form error'
      if (errorCount > 1) {
        msg = `There are ${errorCount} form errors.`
      }

      flashError(msg)
    }
  }

  addFlashMessages(page.props)
})

function shouldBypassErrors(errors) {
  const errorKeys = Object.keys(errors)

  if (errorKeys.length !== 1) {
    return false
  }

  return laravelErrorBagsToIgnore.includes(errorKeys[0])
}
</script>

<template>
  <div class="fixed top-0 right-0 z-60 m-6">
    <transition-group name="slide-fade">
      <div
        v-for="(flash, index) in flashes"
        :key="index"
        class="relative flex items-center pl-3 justify-between mb-4 rounded-xl shadow-card bg-black text-white"
        style="min-width: 240px"
      >
        <button
          class="order-last p-3 text-2xl cursor-pointer focus:outline-none focus:opacity-100"
          @click="removeFlash(flash.id)"
        >
          <Icon name="times" size="small" class="text-white" />
        </button>

        <div class="flex items-center gap-4">
          <Icon
            v-if="flash.type === 'success'"
            name="checkCircle"
            size="small"
            class="text-success"
          />
          <Icon
            v-else-if="flash.type === 'error'"
            name="exclamationTriangle"
            size="small"
            class="text-danger"
          />

          <div class="flex items-center text-sm font-medium">
            {{ flash.message }}
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>
<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s;
}

.slide-fade-enter-from,
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(400px);
  opacity: 0;
}
</style>
